import { RefObject, useCallback, useEffect, useRef, useState } from 'react';

import { generateNanoId } from '../../../../utils/generateNanoId';
import { useCurrentUser } from '../../../../auth/hooks/useAuth';
import { useToastNotification } from '../../../../common/hooks/useToastNotification';
import { composerKeys } from '../../../../locales/keys';
import { useCreateRenderGeneration } from '../../../renderGenerations/hooks/useCreateRenderGeneration';
import { RENDER_GENERATION_POLLING_INTERVAL } from '../../../renderGenerations/renderGenerationsConstants';
import { DEFAULT_REQUESTED_SIZE } from '../../composerConstants';
import { RenderGenerationsBffUrl } from '../../../renderGenerations/RenderGenerationsBffUrl';
import {
  CreateCompositeRenderGenerationInput,
  RenderGenerationStatus,
  RenderGenerationType
} from '../../../renderGenerations/renderGenerationsTypes';
import { useGenerationPolling } from '../../../modelViewers/components/content/ModelViewerEditor/hooks/useModelViewerLifestyleGeneration/useGenerationPolling';
import type { ComposerEmbedHandle } from '../../ComposerEmbed';
import type { GenerationPollResult } from '../../../modelViewers/components/content/ModelViewerEditor/hooks/useModelViewerLifestyleGeneration/useGenerationPolling/useGenerationPolling';
import type { PersistedComposerRenderGeneration } from '../../utils/composerSceneStorage';
import {
  buildCompositeModels,
  buildCompositeCameras,
  buildCompositeBackgroundHex
} from '../../utils/buildCompositeRenderScene';
import { mapCompositeRenderPollResponse } from '../../utils/mapCompositeRenderPollResponse';
import { getCompositeRenderErrorMessage } from '../../utils/getCompositeRenderErrorMessage';

export interface CompositeRenderGenerationItem {
  kind: 'render';
  createdOrder: number;
  localId: string;
  serverId?: string;
  status: RenderGenerationStatus;
  resultPreviewUrl?: string;
  resultFullUrl?: string;
  errorMessage?: string;
}

interface UseComposerRenderGenerationOptions {
  composerRef: RefObject<ComposerEmbedHandle>;
  initialRenderGenerations?: PersistedComposerRenderGeneration[];
  onPersist?: (renderGenerations: PersistedComposerRenderGeneration[]) => void;
  requestedWidth?: number;
  requestedHeight?: number;
}

function toRenderGenerationItem(
  persisted: PersistedComposerRenderGeneration
): CompositeRenderGenerationItem {
  return { kind: 'render', ...persisted };
}

function toPersistedRenderGeneration(
  item: CompositeRenderGenerationItem
): PersistedComposerRenderGeneration {
  return {
    localId: item.localId,
    serverId: item.serverId,
    status: item.status,
    createdOrder: item.createdOrder,
    resultPreviewUrl: item.resultPreviewUrl,
    resultFullUrl: item.resultFullUrl,
    errorMessage: item.errorMessage
  };
}

function useComposerRenderGeneration({
  composerRef,
  initialRenderGenerations,
  onPersist,
  requestedWidth,
  requestedHeight
}: UseComposerRenderGenerationOptions) {
  const currentUser = useCurrentUser();
  const { createRenderGeneration } =
    useCreateRenderGeneration<CreateCompositeRenderGenerationInput>();

  const [isRequesting, setIsRequesting] = useState(false);
  const [renderGenerations, setRenderGenerations] = useState<
    CompositeRenderGenerationItem[]
  >(() => (initialRenderGenerations ?? []).map(toRenderGenerationItem));
  const [activeViewId, setActiveViewId] = useState<string>('scene');

  const { showToastI18nNotification: showSuccessToast } = useToastNotification({
    appearance: 'success'
  });
  const { showToastI18nNotification: showErrorToast } = useToastNotification({
    appearance: 'error'
  });

  const handlePollTick = useCallback(
    (localId: string, result: GenerationPollResult<RenderGenerationStatus>) => {
      setRenderGenerations((prev) =>
        prev.map((g) => {
          if (g.localId !== localId) return g;
          if (
            g.status === result.status &&
            result.status !== RenderGenerationStatus.Completed &&
            result.status !== RenderGenerationStatus.Failed
          ) {
            return g;
          }
          return {
            ...g,
            status: result.status,
            resultPreviewUrl: result.resultPreviewUrl,
            resultFullUrl: result.resultFullUrl,
            errorMessage: result.errorMessage
          };
        })
      );
    },
    []
  );

  const { startPolling, stopPolling } = useGenerationPolling({
    buildShowUrl: RenderGenerationsBffUrl.show,
    mapResponse: mapCompositeRenderPollResponse,
    intervalMs: RENDER_GENERATION_POLLING_INTERVAL,
    onTick: handlePollTick,
    onSuccess: setActiveViewId
  });

  // Resume polling for generations that were still Pending/Processing when
  // the composer was last closed — the server may have finished the job (or
  // failed it) while the sidebar's local state was frozen in localStorage.
  useEffect(() => {
    (initialRenderGenerations ?? []).forEach((generation) => {
      const isUnfinished =
        generation.status === RenderGenerationStatus.Pending ||
        generation.status === RenderGenerationStatus.Processing;

      if (isUnfinished && generation.serverId) {
        startPolling(generation.localId, generation.serverId);
      }
    });
    // Only ever run once, on mount — initialRenderGenerations is a snapshot
    // from localStorage read when the composer opened, not a live prop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onPersistRef = useRef(onPersist);
  onPersistRef.current = onPersist;

  useEffect(() => {
    onPersistRef.current?.(renderGenerations.map(toPersistedRenderGeneration));
  }, [renderGenerations]);

  const handleRequestRender = useCallback(async () => {
    if (isRequesting) return;

    const sceneWithBounds = composerRef.current?.getSceneWithBounds();
    if (!sceneWithBounds || sceneWithBounds.scene.instances.length === 0) {
      showErrorToast(composerKeys.renderRequestFailed);
      return;
    }

    const { scene, bounds } = sceneWithBounds;
    const viewportInfo = composerRef.current?.getViewportInfo();
    const resolvedWidth =
      requestedWidth ?? viewportInfo?.width ?? DEFAULT_REQUESTED_SIZE;
    const resolvedHeight =
      requestedHeight ?? viewportInfo?.height ?? DEFAULT_REQUESTED_SIZE;
    const roundedWidth = Math.round(resolvedWidth);
    const roundedHeight = Math.round(resolvedHeight);
    // Cameras must match the requested aspect ratio, not the live canvas —
    // requestedWidth/Height (from the Aspect Ratio button) take precedence
    // over viewportInfo.aspect whenever they're explicitly set.
    const aspect =
      requestedWidth && requestedHeight
        ? requestedWidth / requestedHeight
        : viewportInfo?.aspect ?? resolvedWidth / resolvedHeight;
    const backgroundHex = buildCompositeBackgroundHex(scene);
    const localId = generateNanoId();

    setRenderGenerations((prev) => [
      {
        kind: 'render',
        createdOrder: Date.now(),
        localId,
        status: RenderGenerationStatus.Pending
      },
      ...prev
    ]);
    setActiveViewId(localId);
    setIsRequesting(true);

    try {
      const input: CreateCompositeRenderGenerationInput = {
        generation_type: RenderGenerationType.CompositeRender,
        user_id: currentUser?.id ?? null,
        requested_width: roundedWidth,
        requested_height: roundedHeight,
        ...(backgroundHex && { background_hex: backgroundHex }),
        incoming_payload: {
          scene: {
            models: buildCompositeModels(scene, bounds),
            cameras: buildCompositeCameras(scene, aspect)
          }
        }
      };

      const response = await createRenderGeneration(input);

      setRenderGenerations((prev) =>
        prev.map((g) =>
          g.localId === localId ? { ...g, serverId: response.id } : g
        )
      );
      startPolling(localId, response.id);

      showSuccessToast(composerKeys.renderRequested);
    } catch (err) {
      const message = getCompositeRenderErrorMessage(err);
      showErrorToast(composerKeys.renderRequestFailed);
      setRenderGenerations((prev) =>
        prev.map((g) =>
          g.localId === localId
            ? {
                ...g,
                status: RenderGenerationStatus.Failed,
                errorMessage: message
              }
            : g
        )
      );
      // eslint-disable-next-line no-console
      console.error('[composer render generation]', err);
    } finally {
      setIsRequesting(false);
    }
  }, [
    isRequesting,
    composerRef,
    currentUser?.id,
    requestedWidth,
    requestedHeight,
    createRenderGeneration,
    startPolling,
    showSuccessToast,
    showErrorToast
  ]);

  // Stops polling and marks the item as cancelled, but keeps the card in the
  // sidebar — mirrors ModelViewerEditor's Stop button (visible only while
  // generating, distinct from Remove). If the user was viewing this
  // generation full-screen, fall back to the scene view since a cancelled
  // generation has no loading state or result to show there.
  const handleStopRenderGeneration = useCallback(
    (localId: string) => {
      stopPolling(localId);
      setRenderGenerations((prev) =>
        prev.map((g) =>
          g.localId === localId
            ? { ...g, status: RenderGenerationStatus.Cancelled }
            : g
        )
      );
      setActiveViewId((prev) => (prev === localId ? 'scene' : prev));
    },
    [stopPolling]
  );

  const handleRemoveRenderGeneration = useCallback(
    (localId: string) => {
      stopPolling(localId);
      setRenderGenerations((prev) => prev.filter((g) => g.localId !== localId));
      setActiveViewId((prev) => (prev === localId ? 'scene' : prev));
    },
    [stopPolling]
  );

  return {
    renderGenerations,
    isRequesting,
    activeViewId,
    setActiveViewId,
    handleRequestRender,
    handleRemoveRenderGeneration,
    handleStopRenderGeneration
  };
}

export default useComposerRenderGeneration;
